import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Select } from '@components/Select';
import { Input } from '@components/Input';
import TeamSelect from '@features/TeamSelect';
import ChannelLocationSelect from '@features/ChannelLocationSelect';
import { useLoadFinished, useTranslation } from '@hooks';
import { channelsActions } from '@actions';

const Container = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateAreas: '"input1" "select1" "select2"',
    gridTemplateColumns: '100%',
    '@media (max-width: 600px)': {
        gridTemplateAreas: '"input1" "select1" "select2"',
        gridGap: '16px',
    },
    '& .team-select': {
        gridArea: 'select2',
    },
});

const WhatsAppBusinessForm = ({ isEdit, selectedChannel, preselectedNumber }) => {
    const { whatsAppBusinessPhoneNumbers, whatsAppBusinessPhoneNumbersLoading } = useSelector(state => state.channels);
    const phoneNumbersFetched = useLoadFinished(whatsAppBusinessPhoneNumbersLoading);

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { control, setValue, getValues } = useFormContext();

    useEffect(() => {
        if (!isEdit) {
            dispatch(channelsActions.getWhatsAppBusinessPhoneNumbers());
        }
    }, []);

    const availablePhoneNumbers = useMemo(
        () => whatsAppBusinessPhoneNumbers.filter(item => item.connectedChannelId === null),
        [whatsAppBusinessPhoneNumbers],
    );

    const phoneNumberOptions = useMemo(
        () => availablePhoneNumbers.map(item => ({ value: item.id, label: item.phoneNumber })),
        [availablePhoneNumbers],
    );

    const selectPhoneNumber = phoneObject => {
        const options = { shouldDirty: true, shouldValidate: true };
        setValue('phoneNumberId', phoneObject.id, options);
        setValue('phoneNumberName', phoneObject.name, options);
        setValue('whatsAppBusinessId', phoneObject.whatsAppBusinessId, options);
        setValue('phoneNumber', phoneObject.phoneNumber, options);
    };

    useEffect(() => {
        if (!preselectedNumber || !phoneNumbersFetched || getValues('phoneNumberId')) return;
        const accountNumbers = availablePhoneNumbers.filter(
            item => item.whatsAppBusinessId === preselectedNumber.whatsAppBusinessId,
        );
        const phoneObject = preselectedNumber.phoneNumberId
            ? availablePhoneNumbers.find(item => item.id === preselectedNumber.phoneNumberId)
            : accountNumbers.length === 1
              ? accountNumbers[0]
              : null;
        if (!phoneObject) return;
        selectPhoneNumber(phoneObject);
        if (!getValues('name')) {
            setValue('name', phoneObject.name, { shouldDirty: true, shouldValidate: true });
        }
    }, [availablePhoneNumbers, preselectedNumber, phoneNumbersFetched]);

    return (
        <Container>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        sx={{ gridArea: 'input1' }}
                    />
                )}
            />
            {isEdit ? (
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.phoneNumber')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isEdit}
                        />
                    )}
                />
            ) : (
                <Controller
                    control={control}
                    name="phoneNumberId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) =>
                                selectPhoneNumber(whatsAppBusinessPhoneNumbers.find(item => item.id === value))
                            }
                            options={phoneNumberOptions}
                            label={t('common.phoneNumber')}
                            fullWidth
                            required
                            disabled={isEdit || whatsAppBusinessPhoneNumbersLoading}
                            error={!!error}
                        />
                    )}
                />
            )}
            <TeamSelect data={selectedChannel} />
            <ChannelLocationSelect data={selectedChannel} />
        </Container>
    );
};

export default WhatsAppBusinessForm;
